import { useCallback, useRef } from 'react';
import { DocumentTextIcon, ArrowTopRightOnSquareIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import api from '../services/api';

// Renders a message's block document. Text is rendered as React children (so
// it's escaped — no HTML injection); only known block types are emitted.
// `mediaBase` is the app's path prefix for uploaded-video URLs ('' on the
// dashboard, '/fsm' in the FSM app).

// A URL written into body text is turned into a link.
//
// Text blocks are rendered as React children, which escapes them -- so a pasted
// URL used to arrive as inert grey text that an engineer could not tap. There
// is a dedicated Link block, but people paste into the paragraph they are
// already writing, and that is the behaviour worth supporting.
//
// Still built from React elements rather than injected HTML: only the matched
// span becomes an anchor, and only for http/https (or a bare www., which is
// given https). Nothing else in the string is interpreted.
//
// The trailing character class keeps sentence punctuation out of the href, so
// "see https://nano.nanofibre.co.uk/fsm." links the URL and leaves the full
// stop behind.
const URL_PATTERN = /(https?:\/\/[^\s<>()[\]]*[^\s<>()[\].,;:!?'"])|(\bwww\.[^\s<>()[\]]*[^\s<>()[\].,;:!?'"])/gi;

function linkify(text) {
  if (typeof text !== 'string' || !text) return text;
  const out = [];
  let last = 0;
  let m;
  URL_PATTERN.lastIndex = 0;
  // eslint-disable-next-line no-cond-assign
  while ((m = URL_PATTERN.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const raw = m[0];
    const href = /^www\./i.test(raw) ? `https://${raw}` : raw;
    out.push(
      <a
        key={`${m.index}-${raw}`}
        href={href}
        target="_blank"
        rel="noreferrer"
        className="break-all text-brand underline"
      >
        {raw}
      </a>
    );
    last = m.index + raw.length;
  }
  if (!out.length) return text;
  if (last < text.length) out.push(text.slice(last));
  return out;
}

// Only allowlisted hosts are embedded in an iframe; anything else is a plain
// link, so a pasted URL can never iframe an arbitrary site.
function embedUrl(url) {
  try {
    const u = new URL(url);
    const h = u.hostname.replace(/^www\./, '');
    if (h === 'youtube.com' || h === 'm.youtube.com') { const v = u.searchParams.get('v'); return v ? `https://www.youtube.com/embed/${v}` : null; }
    if (h === 'youtu.be') { const id = u.pathname.slice(1); return id ? `https://www.youtube.com/embed/${id}` : null; }
    if (h === 'vimeo.com') { const id = u.pathname.split('/').filter(Boolean)[0]; return id ? `https://player.vimeo.com/video/${id}` : null; }
    if (h.endsWith('sharepoint.com') || h.endsWith('microsoftstream.com') || h.endsWith('office.com')) return url;
    return null;
  } catch { return null; }
}

// Playback tracking for videos WE host. Counts distinct whole seconds that
// actually played, so dragging the scrubber to the end does not register as
// watched — which is the specific dodge this exists to stop. Linked videos
// (YouTube/Vimeo, below) play in their own iframe and cannot be tracked at all.
function TrackedVideo({ block, mediaBase, commId, onProgress }) {
  const ref = useRef(null);
  const seen = useRef(new Set());
  const lastPost = useRef(0);

  const post = useCallback(async (force) => {
    const el = ref.current;
    if (!el || !commId) return;
    const now = Date.now();
    if (!force && now - lastPost.current < 10000) return; // throttle: 1 post / 10s
    lastPost.current = now;
    const durationSeconds = Number.isFinite(el.duration) ? Math.floor(el.duration) : null;
    // Send WHICH seconds were seen, not just how many: one bit per second of
    // the video. The server ORs this into what it already holds, so watching
    // the same minute again changes nothing while a new minute adds — and two
    // honest sittings add up instead of the shorter one being discarded.
    let watchedMap;
    if (durationSeconds) {
      const bytes = new Uint8Array(Math.ceil(durationSeconds / 8));
      seen.current.forEach((sec) => {
        if (sec >= 0 && sec < durationSeconds) bytes[sec >> 3] |= 1 << (sec & 7);
      });
      let bin = '';
      bytes.forEach((b) => { bin += String.fromCharCode(b); });
      watchedMap = btoa(bin);
    }
    try {
      const { data } = await api.post(`/api/comms/${commId}/video-progress`, {
        mediaId: block.mediaId, watchedSeconds: seen.current.size, durationSeconds, watchedMap,
      });
      if (onProgress) onProgress(String(block.mediaId), data);
    } catch (e) {
      // Non-fatal: the next timeupdate retries, and watched seconds are held
      // client-side until one lands, so nothing is lost by a dropped post.
    }
  }, [block.mediaId, commId, onProgress]);

  return (
    <video
      ref={ref}
      controls
      playsInline
      className="w-full rounded-md border border-gray-200"
      src={`${mediaBase}/api/comms/media/${block.mediaId}?k=${encodeURIComponent(block.token)}`}
      onPlay={(e) => {
        // One at a time. Without this, several videos in the same message can
        // be started together and all reach 100% in the time of the longest —
        // which is playing them, not watching them.
        document.querySelectorAll('video').forEach((v) => { if (v !== e.target) v.pause(); });
      }}
      onTimeUpdate={(e) => {
        const el = e.target;
        if (el.paused || el.seeking) return;
        // Not counted while the tab is in the background: a video left running
        // in a hidden tab is not being watched either.
        if (typeof document !== 'undefined' && document.hidden) return;
        seen.current.add(Math.floor(el.currentTime));
        post(false);
      }}
      onPause={() => post(true)}
      onEnded={() => post(true)}
    />
  );
}

function Video({ block, mediaBase, commId, onProgress }) {
  if (block.kind === 'upload' && block.mediaId) {
    return <TrackedVideo block={block} mediaBase={mediaBase} commId={commId} onProgress={onProgress} />;
  }
  if (block.kind === 'link' && block.url) {
    const embed = embedUrl(block.url);
    if (embed) {
      return (
        <div className="relative w-full overflow-hidden rounded-md border border-gray-200" style={{ paddingTop: '56.25%' }}>
          <iframe title="Video" src={embed} className="absolute inset-0 h-full w-full" allow="autoplay; encrypted-media; fullscreen; picture-in-picture" allowFullScreen />
        </div>
      );
    }
    return <a href={block.url} target="_blank" rel="noreferrer" className="break-all text-brand underline">{block.url}</a>;
  }
  return null;
}

// A supporting document: a file uploaded with the message (served like uploaded
// videos, by capability token) or a link to one that already exists, usually
// in the Nano Document Library on SharePoint. Only https links are followed.
// The block carries `source`, never `kind` -- the server's video tracking
// treats any kind 'upload' block as a video.
const DOC_LABEL = { pdf: 'PDF', doc: 'Word', docx: 'Word', xls: 'Excel', xlsx: 'Excel', ppt: 'PowerPoint', pptx: 'PowerPoint', txt: 'Text', csv: 'CSV' };
const fmtSize = (n) => (n >= 1024 * 1024 ? `${(n / (1024 * 1024)).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

// With `onOpen` the card opens the in-app viewer (DocumentViewer); without it,
// it falls back to a plain link in a new tab.
function DocumentCard({ block, mediaBase, onOpen }) {
  const href = block.source === 'upload'
    ? (block.mediaId ? `${mediaBase}/api/comms/media/${block.mediaId}?k=${encodeURIComponent(block.token || '')}` : null)
    : (typeof block.url === 'string' && /^https:\/\//i.test(block.url) ? block.url : null);
  if (!href) return null;
  const cardCls = 'flex w-full items-center gap-3 rounded-lg border border-gray-200 bg-white px-3 py-3 text-left no-underline shadow-sm hover:border-brand active:bg-gray-50';
  const name = block.name || '';
  const ext = ((name.match(/\.([a-z0-9]+)$/i) || [])[1] || '').toLowerCase();
  const detail = [
    DOC_LABEL[ext] || 'Document',
    block.size ? fmtSize(block.size) : null,
    block.source === 'library' ? 'Nano Document Library' : null,
  ].filter(Boolean).join(' · ');
  const inner = (
    <>
      <DocumentTextIcon className="h-8 w-8 shrink-0 text-brand" />
      <span className="min-w-0 flex-1">
        <span className="block truncate font-medium text-gray-900">{block.title || name || 'Document'}</span>
        <span className="block truncate text-xs text-gray-500">{detail}</span>
      </span>
      {onOpen ? <ChevronRightIcon className="h-5 w-5 shrink-0 text-gray-400" /> : <ArrowTopRightOnSquareIcon className="h-5 w-5 shrink-0 text-gray-400" />}
    </>
  );
  return onOpen
    ? <button type="button" onClick={() => onOpen(block)} className={cardCls}>{inner}</button>
    : <a href={href} target="_blank" rel="noreferrer" className={cardCls}>{inner}</a>;
}

export default function MessageBlocks({ blocks, mediaBase = '', commId = null, onProgress = null, onOpenDocument = null }) {
  if (!Array.isArray(blocks)) return null;
  return (
    <div className="space-y-3 text-[15px] text-gray-700">
      {blocks.map((b, i) => {
        switch (b.type) {
          case 'heading':
            return <h3 key={b.id || i} className="text-lg font-bold text-gray-900">{b.text}</h3>;
          case 'text':
            return <p key={b.id || i} className="whitespace-pre-wrap leading-relaxed">{linkify(b.text)}</p>;
          case 'bullets':
            return (
              <ul key={b.id || i} className="list-disc space-y-1 pl-5">
                {(b.items || []).filter((x) => x.trim()).map((it, x) => <li key={x}>{linkify(it)}</li>)}
              </ul>
            );
          case 'table':
            return (
              <div key={b.id || i} className="overflow-x-auto">
                <table className="min-w-full border-collapse text-sm">
                  <tbody>
                    {(b.rows || []).map((row, r) => (
                      <tr key={r}>
                        {row.map((cell, c) => (r === 0
                          ? <th key={c} className="border border-gray-300 bg-gray-50 px-2 py-1 text-left font-semibold">{cell}</th>
                          : <td key={c} className="border border-gray-200 px-2 py-1">{cell}</td>))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          case 'image':
            return b.dataUri ? <img key={b.id || i} src={b.dataUri} alt={b.alt || ''} className="max-w-full rounded-md border border-gray-200" /> : null;
          case 'video':
            return <Video key={b.id || i} block={b} mediaBase={mediaBase} commId={commId} onProgress={onProgress} />;
          case 'document':
            return <DocumentCard key={b.id || i} block={b} mediaBase={mediaBase} onOpen={onOpenDocument} />;
          case 'link': {
            // A link shown as wording rather than a raw address, for the many
            // cases where the URL itself is long, ugly, or carries a tracking
            // query nobody needs to read.
            const u = b.url;
            const safe = typeof u === 'string' && (/^\//.test(u) || /^https?:\/\//i.test(u));
            if (!safe) return null;
            const external = /^https?:\/\//i.test(u);
            return (
              <p key={b.id || i}>
                <a
                  href={u}
                  {...(external ? { target: '_blank', rel: 'noreferrer' } : {})}
                  className="break-all text-brand underline"
                >
                  {/* Falls back to the address when no wording was given, so a
                      half-filled block still shows something clickable rather
                      than disappearing from the message. */}
                  {b.text && b.text.trim() ? b.text : u}
                </a>
              </p>
            );
          }
          case 'button': {
            const u = b.url;
            const safe = typeof u === 'string' && (/^\//.test(u) || /^https?:\/\//i.test(u));
            return safe ? (
              <a
                key={b.id || i}
                href={u}
                style={{ backgroundColor: '#711C91' }}
                className="inline-block rounded-md px-4 py-2 text-sm font-semibold text-white no-underline hover:opacity-90"
              >
                {b.label || 'Open'}
              </a>
            ) : null;
          }
          default:
            return null;
        }
      })}
    </div>
  );
}
